import React, { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation, Trans } from "react-i18next";
import { bookInterviewAndRedirect } from "../../utils/interviewBooking";
import "./notification.css";

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" className="medium-approval-icon">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 9h18M8 3v4M16 3v4" />
    <path d="M9 14l2 2 4-4" />
  </svg>
);

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" className="medium-approval-icon">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8h.01M11 12h1v5h1" />
  </svg>
);

const VideoIcon = () => (
  <svg viewBox="0 0 24 24" className="medium-approval-icon">
    <rect x="3" y="5" width="14" height="14" rx="2" />
    <path d="M17 9l4-2v10l-4-2" />
  </svg>
);

const SupportIcon = () => (
  <svg viewBox="0 0 24 24" className="medium-approval-icon">
    <path d="M4 15v-3a8 8 0 0116 0v3" />
    <rect x="2" y="14" width="5" height="6" rx="1.5" />
    <rect x="17" y="14" width="5" height="6" rx="1.5" />
  </svg>
);

// interviewStatus and interviewAppointment both come from the
// `api/profile-info` response (see profile.js's getProfile(), which
// stores that response in its `profileSection` state):
//   interview_status: "Not Requested" -> real "Book 15 Minute Interview" button
//                     "Upcoming"      -> disabled "Upcoming" tile with the
//                                        real appointment date/time + link
//                     "Completed"     -> green "Interview Status: Completed" tile
//   interview_appointment: the appointment object (date, time, timezone,
//                     meeting_link, etc.) - only meaningful when status is
//                     "Upcoming", but this component doesn't assume that;
//                     it just checks whether the object/link exist.
// Defaults to "Not Requested"/null so nothing breaks if either field is
// ever missing (e.g. profileSection still loading on first render).
export default function Notifiacation({
  interviewStatus = "Not Requested",
  interviewAppointment = null,
}) {
  const { t } = useTranslation("mediumRegister");
  const { id } = useSelector((state) => state.user.user);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [showMoreInfo, setShowMoreInfo] = useState(false);

  const handleBookInterview = () => {
    if (bookingLoading) return;
    setBookingLoading(true);
    bookInterviewAndRedirect(id).catch((err) => {
      setBookingLoading(false);
      console.log(err);
      toast.error(
        err.message || t("notification.toasts.interviewLinkError")
      );
    });
  };

  return (
    <div className="medium-approval-wrap">
      <div className="medium-approval-notice">
        {interviewStatus === "Not Requested" ? (
          <>
            <div className="medium-approval-notice-header">
              <span className="medium-approval-notice-icon">
                <InfoIcon />
              </span>
              <div>
                <h3 className="medium-approval-notice-title">
                  {t("notification.underReview")}
                </h3>
                <p className="medium-approval-notice-subtitle">
                  {t("notification.awaitingApproval")}
                </p>
              </div>
            </div>

            <div className="medium-approval-notice-callout">
              <Trans i18nKey="notification.actionNeeded" ns="mediumRegister" components={{ strong: <strong /> }} />
            </div>

            <div className="medium-approval-notice-secondary">
              <p>
                <Trans i18nKey="notification.alreadyScheduled" ns="mediumRegister" components={{ strong: <strong /> }} />
              </p>
              <p>
                {/* TODO: swap in the real Medium Information URL */}
                <Trans
                  i18nKey="notification.recommendReview"
                  ns="mediumRegister"
                  components={{
                    faq: <a href="https://verysoul.com/medium-faqs" target="_blank" rel="noreferrer" />,
                    info: <a href="#" />,
                  }}
                />
              </p>
              <p>
                {t("notification.afterInterview")}
              </p>
            </div>

            <p className="medium-approval-notice-support">
              <Trans
                i18nKey="notification.needHelp"
                ns="mediumRegister"
                components={{
                  strong: <strong />,
                  a: <a href={process.env.REACT_APP_SUPPORT_URL} target="_blank" rel="noreferrer" />,
                }}
              />
            </p>
          </>
        ) : (
          // Shown once the interview is scheduled or completed
          // (interviewStatus is "upcoming" or "completed") - same card,
          // different copy since there's no more booking action needed.
          <>
            <div className="medium-approval-notice-header">
              <span className="medium-approval-notice-icon">
                <InfoIcon />
              </span>
              <div>
                <h3 className="medium-approval-notice-title">
                  {t("notification.awaitingApproval")}
                </h3>
              </div>
            </div>

            <div className="medium-approval-notice-secondary">
              <p>
                <Trans i18nKey="notification.pendingNextStep" ns="mediumRegister" components={{ strong: <strong /> }} />
              </p>
              <p>
                {/* TODO: swap in the real Medium Information URL */}
                <Trans
                  i18nKey="notification.encourageReview"
                  ns="mediumRegister"
                  components={{
                    faq: <a href="https://verysoul.com/medium-faqs" target="_blank" rel="noreferrer" />,
                    info: <a href="#" />,
                  }}
                />
              </p>
              <p>
                {t("notification.onceCompleted")}
              </p>
            </div>

            <p className="medium-approval-notice-support">
              <Trans
                i18nKey="notification.needAssistance"
                ns="mediumRegister"
                components={{
                  strong: <strong />,
                  a: <a href={process.env.REACT_APP_SUPPORT_URL} target="_blank" rel="noreferrer" />,
                }}
              />
            </p>
          </>
        )}
      </div>

      <div className="medium-approval-actions">
        {interviewStatus === "Completed" ? (
          <button
            type="button"
            className="medium-approval-tile medium-approval-tile-completed"
            disabled
          >
            <span className="medium-approval-icon-circle">
              <CalendarIcon />
            </span>
            <span className="medium-approval-label">
              {t("notification.tiles.interviewCompleted")}
            </span>
          </button>
        ) : interviewStatus === "Upcoming" ? (
          // Plain <div>, not a <button>, because it holds a real nested
          // link ("Get Link Here") - a <button> can't legally contain
          // interactive content like an <a>, and a disabled ancestor
          // button would make that link unreliable to click across
          // browsers anyway.
          <div className="medium-approval-tile medium-approval-tile-disabled">
            <span className="medium-approval-icon-circle">
              <CalendarIcon />
            </span>
            <span className="medium-approval-label">{t("notification.tiles.upcoming")}</span>
            <span className="medium-approval-tile-note">
              {interviewAppointment
                ? t("notification.tiles.appointmentAt", {
                    date: interviewAppointment.date,
                    time: interviewAppointment.time,
                    timezone: interviewAppointment.timezone,
                  })
                : t("notification.tiles.appointmentUnavailable")}
            </span>
            {interviewAppointment && interviewAppointment.meeting_link ? (
              <a
                href={interviewAppointment.meeting_link}
                target="_blank"
                rel="noreferrer"
                className="medium-approval-tile-link"
              >
                {t("notification.tiles.getLink")}
              </a>
            ) : (
              // No real link to send them to yet - visual only, matching
              // the pattern used elsewhere in this file for placeholders.
              <a
                href="#"
                className="medium-approval-tile-link"
                onClick={(e) => e.preventDefault()}
              >
                {t("notification.tiles.getLink")}
              </a>
            )}
          </div>
        ) : (
          <button
            type="button"
            className="medium-approval-tile medium-approval-tile-featured"
            onClick={handleBookInterview}
            disabled={bookingLoading}
          >
            <span className="medium-approval-icon-circle">
              <CalendarIcon />
            </span>
            <span className="medium-approval-label">
              {bookingLoading ? t("notification.tiles.loading") : interviewStatus === "Canceled" || interviewStatus === "No Shows" ? t("notification.tiles.bookNewInterview") : t("notification.tiles.bookInterview")}
            </span>
          </button>
        )}

        <button
          type="button"
          className="medium-approval-tile"
          onClick={() => setShowMoreInfo((prev) => !prev)}
          aria-expanded={showMoreInfo}
          aria-controls="medium-approval-links"
        >
          <span className="medium-approval-icon-circle">
            <InfoIcon />
          </span>
          <span className="medium-approval-label">
            {t("notification.tiles.moreInfo")}
          </span>
        </button>

        {/* Video not ready yet - swap href once it's published */}
        <a
          className="medium-approval-tile medium-approval-tile-disabled"
          href="#"
          onClick={(e) => e.preventDefault()}
        >
          <span className="medium-approval-icon-circle">
            <VideoIcon />
          </span>
          <span className="medium-approval-label">
            {t("notification.tiles.faqVideo")}
          </span>
        </a>

        <a
          className="medium-approval-tile"
          href={process.env.REACT_APP_SUPPORT_URL}
          target="_blank"
          rel="noreferrer"
        >
          <span className="medium-approval-icon-circle">
            <SupportIcon />
          </span>
          <span className="medium-approval-label">{t("notification.tiles.contactSupport")}</span>
        </a>
      </div>

      <div
        id="medium-approval-links"
        className={`medium-approval-links${
          showMoreInfo ? " medium-approval-links-open" : ""
        }`}
      >
        <span>
          <Trans
            i18nKey="notification.links.reviewCodeOfEthics"
            ns="mediumRegister"
            components={{ a: <a href="https://verysoul.com/code-of-ethics/" target="_blank" rel="noreferrer" /> }}
          />
        </span>
        <span>
          <Trans
            i18nKey="notification.links.reviewOperatingPrinciples"
            ns="mediumRegister"
            components={{ a: <a href="https://verysoul.com/operating-principle/" target="_blank" rel="noreferrer" /> }}
          />
        </span>
        <span>
          <Trans
            i18nKey="notification.links.reviewMediumFaqs"
            ns="mediumRegister"
            components={{ a: <a href="https://verysoul.com/medium-faqs" target="_blank" rel="noreferrer" /> }}
          />
        </span>
      </div>
    </div>
  );
}
